/**
 * App-managed RBAC catalog — keep in sync with backend/app/core/permission_catalog.py
 */

export interface DataScope {
  entity_type?: string | null;
  device_type?: string | null;
  course_id?: string | null;
  /** allow = include matching rows; block = exclude matching rows */
  mode?: 'allow' | 'block' | string | null;
}

export interface EffectiveAccess {
  restricted: boolean;
  bypass: boolean;
  group_ids: number[];
  permissions: string[];
  data_scopes: DataScope[];
  data_restricted: boolean;
}

export const DEFAULT_EFFECTIVE_ACCESS: EffectiveAccess = {
  restricted: false,
  bypass: false,
  group_ids: [],
  permissions: [],
  data_scopes: [],
  data_restricted: false,
};

export interface TabDef {
  key: string;
  label: string;
}

export interface ModuleDef {
  key: string;
  label: string;
  featureFlag: string;
  tabs: TabDef[];
}

export const MODULE_PERMISSIONS: ModuleDef[] = [
  { key: 'module.dashboard', label: 'Command Center', featureFlag: 'dashboard', tabs: [] },
  { key: 'module.analysis', label: 'Analysis', featureFlag: 'analysis', tabs: [] },
  { key: 'module.scheduledAnalyses', label: 'Scheduled Analysis', featureFlag: 'scheduledAnalyses', tabs: [] },
  { key: 'module.results', label: 'Results', featureFlag: 'results', tabs: [] },
  {
    key: 'module.cop',
    label: 'COP Dashboard',
    featureFlag: 'cop',
    tabs: [
      { key: 'tab.cop.stats', label: 'Stats' },
      { key: 'tab.cop.events', label: 'Events' },
      { key: 'tab.cop.devices', label: 'Devices' },
      { key: 'tab.cop.status', label: 'Status' },
      { key: 'tab.cop.legend', label: 'Legend' },
      { key: 'tab.cop.dwells', label: 'Dwells' },
      { key: 'tab.cop.geofences', label: 'Geofences' },
      { key: 'tab.cop.team_chat', label: 'Team Chat' },
      { key: 'tab.cop.dfp', label: 'DFP' },
      { key: 'tab.cop.rf_feed', label: 'RF Feed' },
    ],
  },
  {
    key: 'module.pathfinder',
    label: 'Pathfinder',
    featureFlag: 'pathfinder',
    tabs: [
      { key: 'tab.pathfinder.charts', label: 'Interactive Charts' },
      { key: 'tab.pathfinder.area_search', label: 'Area Search' },
      { key: 'tab.pathfinder.geofence', label: 'Geofence' },
      { key: 'tab.pathfinder.overwatch', label: 'OSINT' },
      { key: 'tab.pathfinder.co_traveler', label: 'Co-Traveler' },
      { key: 'tab.pathfinder.dwell', label: 'Dwell Analysis' },
      { key: 'tab.pathfinder.biometrics', label: 'Biometrics' },
      { key: 'tab.pathfinder.story_mode', label: 'Story Mode' },
      { key: 'tab.pathfinder.digital_pol', label: 'Digital PoL' },
      { key: 'tab.pathfinder.signal_environment', label: 'Signal Environment' },
      { key: 'tab.pathfinder.annotations', label: 'Annotations' },
    ],
  },
  { key: 'module.linkAnalysis', label: 'Link Analysis', featureFlag: 'linkAnalysis', tabs: [] },
  {
    key: 'module.patternOfLife',
    label: 'Pattern of Life',
    featureFlag: 'patternOfLife',
    tabs: [
      { key: 'tab.pol.entities', label: 'Entities' },
      { key: 'tab.pol.activity', label: 'Activity' },
      { key: 'tab.pol.patterns', label: 'Patterns' },
      { key: 'tab.pol.correlations', label: 'Correlations' },
      { key: 'tab.pol.anomalies', label: 'Anomalies' },
      { key: 'tab.pol.outlier_rankings', label: 'Outlier Rankings' },
      { key: 'tab.pol.forecast', label: 'Forecast' },
      { key: 'tab.pol.routes', label: 'Routes' },
      { key: 'tab.pol.dwells', label: 'Dwells' },
      { key: 'tab.pol.digital', label: 'Digital' },
    ],
  },
  { key: 'module.itinerary', label: 'Itinerary', featureFlag: 'itinerary', tabs: [] },
  { key: 'module.dashboardBuilder', label: 'Dashboard Builder', featureFlag: 'dashboardBuilder', tabs: [] },
  { key: 'module.rfDashboard', label: 'RF Signal Module', featureFlag: 'rfDashboard', tabs: [] },
  { key: 'module.adtech', label: 'AdTech', featureFlag: 'adtech', tabs: [] },
  { key: 'module.overwatch', label: 'OSINT', featureFlag: 'overwatch', tabs: [] },
  { key: 'module.intel', label: 'Intel Packet', featureFlag: 'intel', tabs: [] },
];

export const MODULE_KEY_BY_FEATURE_FLAG: Record<string, string> = Object.fromEntries(
  MODULE_PERMISSIONS.map((m) => [m.featureFlag, m.key])
);

export const FEATURE_FLAG_BY_MODULE_KEY: Record<string, string> = Object.fromEntries(
  MODULE_PERMISSIONS.map((m) => [m.key, m.featureFlag])
);

export const TABS_BY_MODULE: Record<string, string[]> = Object.fromEntries(
  MODULE_PERMISSIONS.map((m) => [m.key, m.tabs.map((t) => t.key)])
);

export const PARENT_MODULE_BY_TAB: Record<string, string> = {};
for (const m of MODULE_PERMISSIONS) {
  for (const t of m.tabs) {
    PARENT_MODULE_BY_TAB[t.key] = m.key;
  }
}

/** Path label → permission key for Pathfinder tool tabs */
export const PATHFINDER_TAB_PERMISSION_BY_LABEL: Record<string, string> = {
  'Interactive Charts': 'tab.pathfinder.charts',
  'Area Search': 'tab.pathfinder.area_search',
  Geofence: 'tab.pathfinder.geofence',
  Overwatch: 'tab.pathfinder.overwatch',
  OSINT: 'tab.pathfinder.overwatch',
  'Co-Traveler': 'tab.pathfinder.co_traveler',
  'Co-Traveler Analysis': 'tab.pathfinder.co_traveler',
  'Dwell Analysis': 'tab.pathfinder.dwell',
  Biometrics: 'tab.pathfinder.biometrics',
  'Story Mode': 'tab.pathfinder.story_mode',
  'Digital PoL': 'tab.pathfinder.digital_pol',
  'Signal Environment': 'tab.pathfinder.signal_environment',
  Annotations: 'tab.pathfinder.annotations',
};

export const COP_TAB_PERMISSION_BY_LABEL: Record<string, string> = {
  Stats: 'tab.cop.stats',
  Events: 'tab.cop.events',
  Devices: 'tab.cop.devices',
  Status: 'tab.cop.status',
  Legend: 'tab.cop.legend',
  Dwells: 'tab.cop.dwells',
  Geofences: 'tab.cop.geofences',
  'Team Chat': 'tab.cop.team_chat',
  DFP: 'tab.cop.dfp',
  'RF Feed': 'tab.cop.rf_feed',
};

export const POL_TAB_PERMISSION_BY_LABEL: Record<string, string> = {
  Entities: 'tab.pol.entities',
  Activity: 'tab.pol.activity',
  Patterns: 'tab.pol.patterns',
  Correlations: 'tab.pol.correlations',
  Anomalies: 'tab.pol.anomalies',
  'Outlier Rankings': 'tab.pol.outlier_rankings',
  Forecast: 'tab.pol.forecast',
  Routes: 'tab.pol.routes',
  Dwells: 'tab.pol.dwells',
  Digital: 'tab.pol.digital',
};

export const CANONICAL_DEVICE_TYPES = ['phone', 'watch', 'vehicle', 'drone'] as const;

const PHONE_ALIASES = new Set([
  'phone',
  'cellphone',
  'cell_phone',
  'smartphone',
  'samsungphone',
  'mobile',
  'student_phone',
]);

export function normalizeAccessDeviceType(raw?: string | null): string | null {
  if (!raw) return null;
  const v = raw.trim().toLowerCase().replace(/\s+/g, '').replace(/-/g, '_');
  if (PHONE_ALIASES.has(v) || v === 'phone') return 'phone';
  if (v === 'watch' || v === 'student_watch') return 'watch';
  if (v === 'vehicle' || v === 'student_vehicle') return 'vehicle';
  if (v === 'drone' || v === 'uas' || v === 'uav' || v === 'aircraft') return 'drone';
  return v;
}

/** Match a device/entity row against allow/block data scopes. */
export function deviceMatchesDataScopes(
  row: {
    entity_type?: string | null;
    device_type?: string | null;
    course_id?: string | null;
    entity_id?: string | null;
    student_id?: string | null;
  },
  access: EffectiveAccess | null | undefined
): boolean {
  // Data scopes apply even when module bypass is set (admins in a group can test blocks).
  if (!access || !access.data_restricted || !access.data_scopes?.length) {
    return true;
  }

  const ENTITY_ALIASES: Record<string, string[]> = {
    student: ['student', 'trainee'],
    trainee: ['student', 'trainee'],
  };

  const rowTypes = new Set<string>();
  const et = (row.entity_type || '').trim().toLowerCase();
  if (et) {
    rowTypes.add(et);
    (ENTITY_ALIASES[et] || []).forEach((a) => rowTypes.add(a));
  }
  const eid = (row.entity_id || row.student_id || '').trim().toLowerCase();
  if (eid.startsWith('student-') || eid.startsWith('trainee-')) {
    rowTypes.add('student');
    rowTypes.add('trainee');
  }

  const dt = normalizeAccessDeviceType(row.device_type);
  const cid = (row.course_id || '').trim();

  const dimMatch = (s: DataScope): boolean => {
    if (s.entity_type) {
      const scopeEt = s.entity_type.trim().toLowerCase();
      const wanted = new Set(ENTITY_ALIASES[scopeEt] || [scopeEt]);
      let hit = false;
      wanted.forEach((w) => {
        if (rowTypes.has(w)) hit = true;
      });
      if (!hit) return false;
    }
    if (s.device_type && normalizeAccessDeviceType(s.device_type) !== dt) return false;
    if (s.course_id && s.course_id.trim() !== cid) return false;
    return true;
  };

  const allows = access.data_scopes.filter((s) => (s.mode || 'allow') !== 'block');
  const blocks = access.data_scopes.filter((s) => (s.mode || 'allow') === 'block');

  if (blocks.some(dimMatch)) return false;
  if (allows.length === 0) return true;
  return allows.some(dimMatch);
}

export function filterByDataScopes<T extends {
  entity_type?: string | null;
  device_type?: string | null;
  course_id?: string | null;
  entity_id?: string | null;
  student_id?: string | null;
}>(rows: T[], access: EffectiveAccess | null | undefined): T[] {
  if (!access || !access.data_restricted) return rows;
  return rows.filter((r) => deviceMatchesDataScopes(r, access));
}

export function hasPermission(access: EffectiveAccess | null | undefined, key: string): boolean {
  if (!access || access.bypass || !access.restricted) return true;
  const perms = new Set(access.permissions || []);
  if (perms.has(key)) return true;
  const parent = PARENT_MODULE_BY_TAB[key];
  if (parent && perms.has(parent)) {
    const siblings = TABS_BY_MODULE[parent] || [];
    if (!siblings.some((t) => perms.has(t))) return true;
  }
  return false;
}

export function hasModulePermission(
  access: EffectiveAccess | null | undefined,
  moduleKey: string
): boolean {
  if (!access || access.bypass || !access.restricted) return true;
  const perms = new Set(access.permissions || []);
  if (perms.has(moduleKey)) return true;
  return (TABS_BY_MODULE[moduleKey] || []).some((t) => perms.has(t));
}

export function hasFeatureFlagPermission(
  access: EffectiveAccess | null | undefined,
  featureFlagId: string
): boolean {
  const moduleKey = MODULE_KEY_BY_FEATURE_FLAG[featureFlagId];
  if (!moduleKey) return true;
  return hasModulePermission(access, moduleKey);
}

/** Map route path → module permission key */
export const PATH_TO_MODULE_KEY: Record<string, string> = {
  '/dashboard': 'module.dashboard',
  '/analysis': 'module.analysis',
  '/scheduled-analyses': 'module.scheduledAnalyses',
  '/results': 'module.results',
  '/cop': 'module.cop',
  '/pathfinder': 'module.pathfinder',
  '/intel': 'module.intel',
  '/missions': 'module.intel',
  '/link-analysis': 'module.linkAnalysis',
  '/pattern-of-life': 'module.patternOfLife',
  '/itinerary': 'module.itinerary',
  '/content': 'module.dashboardBuilder',
  '/force-protection': 'module.rfDashboard',
};
